import React, { useEffect, useMemo, useState } from 'react';
import { Input, Pagination, Select, Typography } from '@attentive-platform/stem-ui';

import useScopeRecords, { PAGE_SIZE_OPTIONS } from './useScopeRecords';
import { RecordsTabStyles as R, TableStyles as T } from './styles';

const COLUMNS = [
  { key: 'company', label: 'Company' },
  { key: 'trades', label: 'Trades scoped' },
  { key: 'version', label: 'Current version' },
  { key: 'updated', label: 'Updated' },
  { key: 'pilot', label: 'Pilot' },
  { key: 'headroom', label: 'Complexity · price' }
];

const RecordsTab = ({ onOpenRow, refreshToken }) => {
  const [query, setQuery] = useState('');
  const { rows, count, loading, refetch, page, setPage, pageSize, setPageSize, pageCount } = useScopeRecords({
    linked: true,
    query,
    stateFilter: 'All states',
    userFilter: 'All internal users',
    pilotFilter: 'All pilots',
    dateFilter: 'All time'
  });

  useEffect(() => {
    if (refreshToken !== undefined) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const rowsLabel = count === 1 ? '1 company record' : `${count} company records`;
  const pageSizeItems = useMemo(
    () => PAGE_SIZE_OPTIONS.map(n => ({ label: `${n} / page`, value: n })),
    []
  );

  return (
    <T.Wrap>
      <T.Toolbar>
        <T.ToolbarLeft>
          <Typography variant='subtitle1-medium'>Company scope records</Typography>
          <Typography variant='body3' color='#666666'>
            Submissions linked to a registered company — the scope of record each version, override and exception
            attaches to.
          </Typography>
        </T.ToolbarLeft>
        {/* <T.SearchNarrow> */}
        <Input
          size='large'
          placeholder='Search company or customer email'
          value={query}
          onChange={e => setQuery(e.target.value)}
          width='20rem'
        />
        {/* </T.SearchNarrow> */}
      </T.Toolbar>

      <T.TableBox>
        <R.HeaderGrid>
          {COLUMNS.map(c => (
            <T.HeaderCell key={c.key}>{c.label}</T.HeaderCell>
          ))}
        </R.HeaderGrid>
        {loading ? (
          <T.EmptySmall>Loading scope records…</T.EmptySmall>
        ) : rows.length === 0 ? (
          <T.EmptySmall>No linked scope records yet.</T.EmptySmall>
        ) : (
          rows.map(r => {
            const trades = r.tradeList.join(', ') || '—';
            const version = r.version?.version != null ? `v${r.version.version}` : 'v1';
            const versionKind =
              r.state === 'Reopened' ? 'awaiting customer' : r.version?.actor ? `by ${r.version.actor}` : '';
            const Row =
              r.pilotStatus === 'closed' ||
              r.pilotStatus === 'closed_won' ||
              r.pilotStatus === 'closed_lost'
                ? R.RowClosed
                : R.RowOpen;
            return (
              <Row key={r.id} onClick={() => onOpenRow && onOpenRow(r.id)}>
                <R.CompanyText>{r.company}</R.CompanyText>
                <R.TradesText>{trades}</R.TradesText>
                <T.CellStack>
                  <Typography variant='body3' sx={{ fontWeight: 500 }}>
                    {version}
                  </Typography>
                  {versionKind && (
                    <Typography variant='body5' color='#808080'>
                      {versionKind}
                    </Typography>
                  )}
                </T.CellStack>
                <T.Cell>
                  <Typography variant='body3' color='#4D4D4D'>
                    {r.updated || r.created || '—'}
                  </Typography>
                </T.Cell>
                <T.Cell>
                  <Typography variant='body4' color='#4D4D4D'>
                    {r.pilot}
                  </Typography>
                </T.Cell>
                <T.Cell>
                  <Typography variant='body4' color='#9E9E9E'>
                    Phase 4
                  </Typography>
                </T.Cell>
              </Row>
            );
          })
        )}
      </T.TableBox>
      <T.FooterRow>
        <Typography variant='body3' color='#666666'>
          {rowsLabel}
        </Typography>
        <T.FooterRight>
          <T.SelectSmall>
            <Select
              size='small'
              variant='std-filled'
              items={pageSizeItems}
              value={pageSize}
              onChange={next => setPageSize(Number(next))}
            />
          </T.SelectSmall>
          {pageCount > 1 && (
            <Pagination
              size='small'
              color='primary'
              count={pageCount}
              page={page}
              onChange={(_, next) => setPage(next)}
            />
          )}
        </T.FooterRight>
      </T.FooterRow>
      <Typography variant='body4' color='#9E9E9E' sx={{ display: 'inline-block', paddingTop: '12px' }}>
        Complexity index and band price land in this table in Phase 4 — the right-hand column is holding their place.
      </Typography>
    </T.Wrap>
  );
};

export default RecordsTab;
